import { Link } from 'react-router-dom';

import Avatar from 'components/@extended/Avatar';
import { useLearningMode } from 'components/learning-mode/useLearningMode';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { CircleHelpIcon, LogOutIcon, SettingsIcon } from 'components/icons';
import { SecurityModal } from 'components/modals/user/SecurityModal';
import { useModal } from 'components/modals/useModal';
import UserPreferencesModal from 'components/modals/user/UserPreferencesModal';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth';
import { useUiDesign } from 'hooks/useUiDesign';
import { useTour } from 'tour/TourProvider';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { FOCUS_RING } from '../focusRing';
import NewDesignRow from './NewDesignRow';

const ICON_BUTTON = cn(
    'text-sidebar-foreground hover:bg-sidebar-hover hit-area relative flex size-8 flex-none cursor-pointer items-center justify-center rounded-md transition-[scale] duration-(--press-dur) ease-out motion-safe:not-aria-[haspopup]:will-change-transform motion-safe:active:not-aria-[haspopup]:scale-(--press-scale)',
    FOCUS_RING
);

/** The signed-in user's row: avatar, name and job position, opening the profile menu the classic header has. */
function ProfileMenu({ collapsed }: { collapsed: boolean }) {
    const { user, logout } = useAuth();
    const { openModal, closeModal } = useModal();
    const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;
    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.error(err);
        }
    };
    // The initials are drawn, not read: the name beside them, or the trigger's label on the rail, names the button.
    const avatar = (
        <Avatar aria-hidden className="bg-primary text-primary-foreground text-caption size-7 flex-none font-semibold">
            {initials}
        </Avatar>
    );

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label={collapsed ? user?.name : undefined}
                className={cn(
                    'hover:bg-sidebar-hover flex min-w-0 items-center rounded-md text-start',
                    FOCUS_RING,
                    // Open, it is 40px tall and wide, and a `hit-area` over it would take the name's own tooltip.
                    collapsed ? 'hit-area relative size-9 justify-center' : 'flex-1 gap-2.5 px-2 py-1.5'
                )}
            >
                {avatar}
                {!collapsed && (
                    <span className="sidebar-fade flex min-w-0 flex-col leading-[1.25]">
                        <span className="font-semibold">
                            <EllipsisTooltip>{user?.name}</EllipsisTooltip>
                        </span>
                        {user?.jobPositionName ? (
                            <span className="text-sidebar-caption text-caption leading-[1.25]">
                                <EllipsisTooltip>{user.jobPositionName}</EllipsisTooltip>
                            </span>
                        ) : null}
                    </span>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent side={collapsed ? 'right' : 'top'} align="start" className="w-auto min-w-48">
                <DropdownMenuItem onClick={() => openModal(<SecurityModal onClose={closeModal} />, closeModal)}>
                    {t('user.security-title')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openModal(<UserPreferencesModal onClose={closeModal} />, closeModal)}>
                    {t('user.preferences-selection')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void handleLogout()}>
                    <LogOutIcon />
                    {t('menu.logout')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/** Help: the guided tour, the help center and, where the tenant has it, learning mode. */
function HelpMenu({ collapsed }: { collapsed: boolean }) {
    const { start } = useTour();
    const learningMode = useLearningMode();
    const helpLabel = t('help-center.title');

    return (
        <DropdownMenu>
            <Tooltip content={helpLabel} side={collapsed ? 'right' : 'top'}>
                <DropdownMenuTrigger aria-label={helpLabel} className={ICON_BUTTON}>
                    <CircleHelpIcon className="size-[17px]" />
                </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent side={collapsed ? 'right' : 'top'} align="start" className="w-auto min-w-48">
                <DropdownMenuItem onClick={() => void start('help')}>{t('tour.take')}</DropdownMenuItem>
                <DropdownMenuItem render={<Link to="/help-center" />}>{t('help.help-center')}</DropdownMenuItem>
                {learningMode.isAvailable && (
                    // The row's switch is drawn, not a control: the item itself is the checkbox, so it stays one menu stop.
                    <DropdownMenuCheckboxItem
                        checked={learningMode.isEnabled}
                        onCheckedChange={(checked) => learningMode.setIsEnabled(checked)}
                        className="px-2.5 *:data-[slot=dropdown-menu-checkbox-item-indicator]:hidden"
                    >
                        <span className="flex-1">{t('learning-mode.title')}</span>
                        <span
                            aria-hidden
                            className={cn(
                                'flex h-[18px] w-[30px] flex-none items-center rounded-full px-0.5 transition-colors',
                                learningMode.isEnabled ? 'bg-primary' : 'bg-grey-500'
                            )}
                        >
                            <span
                                className={cn(
                                    'bg-background shadow-segment size-3.5 rounded-full motion-safe:transition-[translate] motion-safe:duration-150 motion-safe:ease-out',
                                    learningMode.isEnabled && 'translate-x-3 rtl:-translate-x-3'
                                )}
                            />
                        </span>
                    </DropdownMenuCheckboxItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

type Props = {
    collapsed: boolean;
};

/**
 * The sidebar's last rows: the New design switch, then the profile menu, Help and the gear, which opens the
 * user's own preferences as the profile menu's entry does. On the rail the avatar comes last.
 */
function SidebarFooter({ collapsed }: Props) {
    const { canToggle } = useUiDesign();
    const { openModal, closeModal } = useModal();
    const preferencesLabel = t('user.preferences-selection');

    return (
        // On touch the controls' 44px areas would overlap across `gap-0.5` and `gap-1`: the gaps widen so they meet.
        <div className={cn('flex shrink-0 flex-col', collapsed && 'items-center gap-0.5 pb-2.5 pointer-coarse:gap-3')}>
            {canToggle && (
                <div className={collapsed ? undefined : 'px-3 pb-2'}>
                    <NewDesignRow collapsed={collapsed} />
                </div>
            )}
            <div
                className={cn(
                    'flex items-center',
                    collapsed
                        ? 'flex-col gap-0.5 pointer-coarse:gap-3'
                        : 'border-sidebar-border h-[60px] gap-1 border-t px-3 pointer-coarse:gap-3'
                )}
            >
                {!collapsed && <ProfileMenu collapsed={false} />}
                <HelpMenu collapsed={collapsed} />
                <Tooltip content={preferencesLabel} side={collapsed ? 'right' : 'top'}>
                    <button
                        type="button"
                        aria-label={preferencesLabel}
                        onClick={() => openModal(<UserPreferencesModal onClose={closeModal} />, closeModal)}
                        className={ICON_BUTTON}
                    >
                        <SettingsIcon className="size-[17px]" />
                    </button>
                </Tooltip>
                {collapsed && <ProfileMenu collapsed />}
            </div>
        </div>
    );
}

export default SidebarFooter;
